import Papa from 'papaparse';
import { BASELINE_COLUMNS } from 'views/bulk_import/utils/columnSpec';
import { downloadAsFile } from 'utils/downloadAsFile';
import { utcToUserTzDate } from 'utils/timezone';

/**
 * CSV headers: `network` followed by the bulk-import baseline columns, so the
 * export stays structurally aligned with BulkImportModal's expected format.
 */
const HEADERS = ['network', ...BASELINE_COLUMNS.map((c) => c.name)];

const getAssetUrls = (post) =>
    (post?.assets || [])
        .map((a) => a?.image?.url || a?.video?.url)
        .filter(Boolean);

/**
 * Build the schedule columns (year/month/day/hour/minute) from a post group's
 * `publish_at`, expressed in the user's timezone. Returns empty strings when the
 * group has no scheduled date (the importer auto-queues those).
 */
const getScheduleParts = (publishAt, user) => {
    if (!publishAt) return { year: '', month: '', day: '', hour: '', minute: '' };
    const d = utcToUserTzDate(publishAt, user);
    return {
        year: d.getFullYear(),
        month: d.getMonth() + 1,
        day: d.getDate(),
        hour: d.getHours(),
        minute: d.getMinutes(),
    };
};

/**
 * Map post groups to CSV rows — one row per post (i.e. per social network).
 *
 * @param {Array} postGroups - post groups (API shape, with nested posts/assets)
 * @param {Object} user - current user (for timezone conversion)
 * @returns {Array<Object>} rows keyed by HEADERS
 */
export const buildPostGroupsCsvRows = (postGroups = [], user) => {
    const rows = [];
    (postGroups || []).forEach((group) => {
        const schedule = getScheduleParts(group?.publish_at, user);
        (group?.posts || []).forEach((post) => {
            rows.push({
                network: post?.social_network_type || '',
                content: post?.description || '',
                title: post?.title || '',
                link: post?.link || '',
                media_urls: getAssetUrls(post).join(','),
                ...schedule,
                first_comment: post?.comments_to_create?.[0] || '',
            });
        });
    });
    return rows;
};

/**
 * Generate and download a CSV for the given post groups.
 *
 * @param {Array} postGroups - selected post groups
 * @param {Object} user - current user (timezone)
 * @param {string} [filename]
 */
export const exportPostGroupsToCsv = (
    postGroups,
    user,
    filename = `followr-posts-export-${new Date().toISOString().slice(0, 10)}.csv`
) => {
    const rows = buildPostGroupsCsvRows(postGroups, user);
    const csv = Papa.unparse(
        { fields: HEADERS, data: rows.map((row) => HEADERS.map((h) => row[h] ?? '')) },
        { quotes: true }
    );
    downloadAsFile(csv, filename, 'text/csv;charset=utf-8;');
};

export default exportPostGroupsToCsv;
